/*
 * Keyspan v2 cart drawer controller.
 *
 * Turns the standalone /cart page into a right-side drawer. Opens when:
 *   - the header cart icon (or any [data-ks-cart-open]) is clicked
 *   - any add-to-cart form on the site is submitted (AJAX-intercepted)
 *   - an existing add-to-cart flow dispatches `keyspan:cart:updated` / `cart:refresh`
 *
 * Refreshes its contents via the Shopify Section Rendering API
 * (section id = "keyspan-ai-v2-cart-drawer"), swapping only [data-ks-cart-body]
 * so the open state survives updates.
 */
(function () {
  if (window.__ksCartDrawerInit) return;
  window.__ksCartDrawerInit = true;

  var SECTION_ID = 'keyspan-ai-v2-cart-drawer';

  function routeBase() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }
  function addUrl() {
    return ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
  }
  function changeUrl() {
    return ((window.routes && window.routes.cart_change_url) || '/cart/change') + '.js';
  }

  var els = {};
  var lastFocus = null;
  var savedOverflow = '';

  function q(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function getDrawer() {
    return document.querySelector('[data-ks-cart]');
  }

  /* ---------------------------------------------------------------- open/close */

  function isOpen() {
    var d = getDrawer();
    return !!d && d.classList.contains('is-open');
  }

  function openDrawer() {
    var d = getDrawer();
    if (!d) return;
    if (d.classList.contains('is-open')) return; // already open — don't re-save scroll state
    lastFocus = document.activeElement;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    var closeBtn = q('[data-ks-cart-close]:not(.ks-cart__backdrop)', d);
    if (closeBtn && closeBtn.focus) {
      try { closeBtn.focus({ preventScroll: true }); } catch (e) { closeBtn.focus(); }
    }
  }

  function closeDrawer() {
    var d = getDrawer();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    clearError();
    document.body.style.overflow = savedOverflow;
    if (lastFocus && lastFocus.focus) {
      try { lastFocus.focus({ preventScroll: true }); } catch (e) {}
    }
  }

  /* --------------------------------------------------------------- refreshing */

  function setLoading(state) {
    var d = getDrawer();
    if (d) d.setAttribute('data-ks-cart-loading', state ? 'true' : 'false');
  }

  function updateBadges(count) {
    var n = parseInt(count, 10) || 0;
    document.querySelectorAll('[data-ks-cart-badge]').forEach(function (b) {
      b.textContent = n;
      b.hidden = n <= 0;
      b.setAttribute('data-count', n);
    });
  }

  // The tail (express buttons + trust badges) lives OUTSIDE the swapped body so
  // the accelerated-checkout web components survive refreshes; show it only when
  // there's something to check out.
  function updateTail(count) {
    var d = getDrawer();
    var tail = d && q('[data-ks-cart-tail]', d);
    if (tail) tail.hidden = (parseInt(count, 10) || 0) <= 0;
  }

  function showError(msg) {
    var d = getDrawer();
    var el = d && q('[data-ks-cart-error]', d);
    if (!el) return;
    el.textContent = msg || 'Something went wrong. Please try again.';
    el.hidden = false;
  }

  function clearError() {
    var d = getDrawer();
    var el = d && q('[data-ks-cart-error]', d);
    if (el) el.hidden = true;
  }

  // Monotonic request sequence: only the most recently *initiated* request is
  // allowed to write to the DOM, so a slow GET refresh can't clobber the drawer
  // with stale contents after a newer add/change has already updated it.
  var swapSeq = 0;
  function nextSeq() { return ++swapSeq; }

  // Remember which control had focus before a swap so we can restore it after
  // the body innerHTML is replaced (otherwise focus falls back to <body>).
  function captureFocus() {
    var d = getDrawer();
    var active = document.activeElement;
    if (!d || !active || !d.contains(active) || !active.getAttribute) return function () {};
    var key = active.getAttribute('data-ks-key');
    var kind = active.hasAttribute('data-ks-cart-minus') ? 'minus'
      : active.hasAttribute('data-ks-cart-plus') ? 'plus'
      : active.hasAttribute('data-ks-cart-remove') ? 'remove'
      : (active.classList && active.classList.contains('ks-cart__checkout')) ? 'checkout'
      : null;
    if (!kind) return function () {};
    return function () {
      var dd = getDrawer();
      if (!dd) return;
      var target = null;
      if (kind === 'checkout') {
        target = q('.ks-cart__checkout', dd);
      } else if (key) {
        var esc = (window.CSS && CSS.escape) ? CSS.escape(key) : key.replace(/"/g, '\\"');
        target = dd.querySelector('[data-ks-cart-' + kind + '][data-ks-key="' + esc + '"]');
      }
      if (!target || target.disabled) {
        target = q('[data-ks-cart-close]:not(.ks-cart__backdrop)', dd);
      }
      if (target && target.focus) {
        try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
      }
    };
  }

  // Replace the live drawer body with the body from a freshly rendered section.
  // `seq` guards against out-of-order responses; pass the value returned by
  // nextSeq() when the request was initiated.
  function swapBodyFromHTML(html, seq) {
    var d = getDrawer();
    if (!d || !html) return;
    if (seq !== undefined && seq !== swapSeq) return; // a newer request already won
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('[data-ks-cart-body]');
    var live = q('[data-ks-cart-body]', d);
    if (fresh && live) {
      var restore = captureFocus();
      live.innerHTML = fresh.innerHTML;
      var count = fresh.getAttribute('data-ks-cart-count');
      live.setAttribute('data-ks-cart-count', count || '0');
      updateBadges(count);
      updateTail(count);
      restore();
    }

    // The express buttons live in the persistent tail (not swapped) so they stay
    // functional. But if they rendered empty at page load (cart was empty then),
    // fill them from this fresh render — only when currently empty, so we never
    // wipe already-working buttons.
    var liveExpress = q('.ks-cart__express', d);
    var liveEmpty = !liveExpress || !liveExpress.innerHTML.trim();
    if (liveEmpty) {
      var freshExpress = parsed.querySelector('.ks-cart__express');
      if (freshExpress && freshExpress.innerHTML.trim()) {
        var tail = q('[data-ks-cart-tail]', d);
        if (liveExpress) {
          liveExpress.innerHTML = freshExpress.innerHTML;
        } else if (tail) {
          tail.insertBefore(freshExpress, tail.firstChild);
        }
      }
    }
  }

  // Pull the rendered section from the Section Rendering API (used after flows we
  // don't control, e.g. the shop grid's own AJAX add).
  function refreshFromServer() {
    var seq = nextSeq();
    var url = routeBase().replace(/\/$/, '') + '?section_id=' + SECTION_ID;
    return fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) { swapBodyFromHTML(html, seq); })
      .catch(function () {});
  }

  // Coalesce refreshes triggered by external events that may fire together
  // (e.g. the shop grid dispatches both keyspan:cart:updated and cart:refresh).
  var refreshQueued = false;
  var refreshWantsOpen = false;
  function scheduleRefresh(open) {
    refreshWantsOpen = refreshWantsOpen || !!open;
    if (refreshQueued) return;
    refreshQueued = true;
    setTimeout(function () {
      refreshQueued = false;
      var shouldOpen = refreshWantsOpen;
      refreshWantsOpen = false;
      refreshFromServer().then(function () {
        if (shouldOpen) openDrawer();
      });
    }, 0);
  }

  /* ------------------------------------------------------------ cart mutations */

  var addInFlight = false;

  function postAddForm(form) {
    if (addInFlight) return; // guard against rapid double-submits / re-entrancy
    addInFlight = true;
    clearError();

    var body = new FormData(form);
    body.append('sections', SECTION_ID);
    body.append('sections_url', window.location.pathname);
    setLoading(true);
    var seq = nextSeq();

    fetch(addUrl(), {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) {
            // The add itself was rejected (sold out, invalid selling plan, etc.).
            var err = new Error('cart-add-rejected');
            err.addRejected = true;
            err.data = data;
            throw err;
          }
          return data;
        });
      })
      .then(function (data) {
        // Success — the item is in the cart. Render defensively: a rendering
        // error here must NOT fall through to a native submit (double-add).
        addInFlight = false;
        setLoading(false);
        try {
          if (data.sections && data.sections[SECTION_ID]) {
            swapBodyFromHTML(data.sections[SECTION_ID], seq);
          } else {
            refreshFromServer();
          }
        } catch (e) {
          refreshFromServer();
        }
        openDrawer();
        // Notify other cart-count widgets without re-refreshing our own drawer.
        try {
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { internal: true } }));
        } catch (e) {}
      })
      .catch(function (err) {
        addInFlight = false;
        setLoading(false);
        if (err && err.addRejected) {
          // Add failed for a real reason — show it in the drawer instead of
          // ejecting the customer to a full-page reload.
          var msg = (err.data && (err.data.description || err.data.message)) || null;
          showError(msg);
          openDrawer();
          refreshFromServer();
        } else {
          // Network/parse error before we know the outcome — fall back to a
          // native submit so the add still goes through the normal path.
          form.submit();
        }
      });
  }

  function changeLine(key, quantity) {
    clearError();
    setLoading(true);
    var seq = nextSeq();
    return fetch(changeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: quantity,
        sections: SECTION_ID,
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.sections && data.sections[SECTION_ID]) {
          swapBodyFromHTML(data.sections[SECTION_ID], seq);
        } else {
          return refreshFromServer();
        }
      })
      .then(function () { setLoading(false); })
      .catch(function () { setLoading(false); });
  }

  /* ----------------------------------------------------------------- bindings */

  function onDocumentClick(e) {
    // Open triggers: header cart icon or anything opted in.
    var opener = e.target.closest('[data-ks-cart-open], .keyspan-ai-v2-header-cart');
    if (opener) {
      // Let modified / non-primary clicks fall through so the browser can still
      // open the /cart page in a new tab/window (the trigger is a real <a href>).
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button && e.button !== 0)) {
        return;
      }
      e.preventDefault();
      openDrawer();
      refreshFromServer();
      return;
    }

    var d = getDrawer();
    if (!d) return;

    // Close (X, backdrop). Anchors keep their default navigation.
    var closer = e.target.closest('[data-ks-cart-close]');
    if (closer && d.contains(closer)) {
      if (closer.tagName !== 'A') e.preventDefault();
      closeDrawer();
      return;
    }

    // Only the controls below live inside the drawer.
    if (!d.contains(e.target)) return;

    var removeBtn = e.target.closest('[data-ks-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      changeLine(removeBtn.getAttribute('data-ks-key'), 0);
      return;
    }

    var minusBtn = e.target.closest('[data-ks-cart-minus]');
    if (minusBtn) {
      e.preventDefault();
      var row = minusBtn.closest('[data-ks-cart-item]');
      var numEl = row && q('[data-ks-cart-qty-num]', row);
      var current = numEl ? parseInt(numEl.textContent, 10) || 1 : 1;
      if (current <= 1) {
        changeLine(minusBtn.getAttribute('data-ks-key'), 0);
      } else {
        changeLine(minusBtn.getAttribute('data-ks-key'), current - 1);
      }
      return;
    }

    var plusBtn = e.target.closest('[data-ks-cart-plus]');
    if (plusBtn) {
      e.preventDefault();
      var row2 = plusBtn.closest('[data-ks-cart-item]');
      var numEl2 = row2 && q('[data-ks-cart-qty-num]', row2);
      var current2 = numEl2 ? parseInt(numEl2.textContent, 10) || 0 : 0;
      changeLine(plusBtn.getAttribute('data-ks-key'), current2 + 1);
      return;
    }
  }

  // Global add-to-cart interceptor. Skips forms already handled by a more
  // specific script (those call preventDefault first, so defaultPrevented is set)
  // and forms that opt out with [data-ks-cart-skip].
  function onDocumentSubmit(e) {
    if (e.defaultPrevented) return;
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (form.hasAttribute('data-ks-cart-skip')) return;
    var action = (form.getAttribute('action') || '').split('?')[0];
    if (action.indexOf('/cart/add') === -1) return;

    e.preventDefault();
    postAddForm(form);
  }

  function onKeydown(e) {
    if (!isOpen()) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closeDrawer();
      return;
    }

    // Trap Tab focus within the panel so it can't reach the page behind the
    // modal overlay (aria-modal contract).
    if (e.key !== 'Tab') return;
    var d = getDrawer();
    var panel = d && q('.ks-cart__panel', d);
    if (!panel) return;
    var focusables = Array.prototype.slice
      .call(panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ))
      .filter(function (el) { return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement; });
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (!panel.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function init() {
    els.drawer = getDrawer();

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('submit', onDocumentSubmit);
    document.addEventListener('keydown', onKeydown);

    // Existing flows (e.g. the shop grid) add via their own AJAX and just
    // announce it — open + refresh the drawer in response.
    document.addEventListener('keyspan:cart:updated', function () { scheduleRefresh(true); });
    document.addEventListener('cart:refresh', function (e) {
      // Skip refreshes we dispatched ourselves after already updating the drawer.
      if (e && e.detail && e.detail.internal) return;
      scheduleRefresh(false);
    });

    // Reflect current count in the header badge + tail visibility on load.
    var body = q('[data-ks-cart-body]', els.drawer || document);
    if (body) {
      updateBadges(body.getAttribute('data-ks-cart-count'));
      updateTail(body.getAttribute('data-ks-cart-count'));
    }

    // Auto-open after a redirect-based add (e.g. discount-coded packs).
    try {
      if (sessionStorage.getItem('ksCartOpen') === '1') {
        sessionStorage.removeItem('ksCartOpen');
        openDrawer();
        refreshFromServer();
      }
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
